import React, { useState, useEffect } from 'react';
import { Battery, BatteryCharging, Zap, Flame, Clock } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from 'recharts';

const API_URL = import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api');

export default function WPrimeWidget({ activityId, initialWBalMin, initialWBalExpended }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!activityId) return;

        let isMounted = true;
        setLoading(true);
        setError(null);

        const token = localStorage.getItem('token');
        fetch(`${API_URL}/activities/${activityId}/w-bal`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(res => {
                if (!res.ok) throw new Error('No hay stream de potencia para modelar W\'bal');
                return res.json();
            })
            .then(resData => {
                if (isMounted) {
                    setData(resData);
                    setLoading(false);
                }
            })
            .catch(err => {
                if (isMounted) {
                    setError(err.message);
                    setLoading(false);
                }
            });

        return () => { isMounted = false; };
    }, [activityId]);

    const formatTime = (sec) => {
        const m = Math.floor(sec / 60);
        const s = sec % 60;
        return `${m}:${s < 10 ? '0' : ''}${s}`;
    };

    if (loading) {
        return (
            <div style={{
                background: 'rgba(255, 255, 255, 0.015)',
                border: '1px solid var(--glass-border)',
                borderRadius: '12px',
                padding: '1rem',
                textAlign: 'center'
            }}>
                <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>Calculando Batería Anaeróbica W'bal...</span>
            </div>
        );
    }

    if ((error && !initialWBalMin) || (!loading && !data && !initialWBalMin)) {
        return (
            <div style={{
                background: 'rgba(255, 255, 255, 0.015)',
                border: '1px dashed var(--glass-border)',
                borderRadius: '12px',
                padding: '1.25rem',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
            }}>
                <div style={{ padding: '8px', background: 'rgba(168, 85, 247, 0.1)', borderRadius: '10px', color: '#c084fc' }}>
                    <BatteryCharging size={20} />
                </div>
                <div>
                    <span style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
                        Batería Anaeróbica W'bal no disponible
                    </span>
                    <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.6)', lineHeight: 1.4 }}>
                        El modelo dinámico de Skiba requiere potenciómetro en la sesión y valores de FTP y W' configurados en tu perfil.
                    </p>
                </div>
            </div>
        );
    }

    const minPct = data?.minWBalPct !== undefined ? data.minWBalPct : initialWBalMin;
    const workAboveCp = data?.totalWorkAboveCpKj !== undefined ? data.totalWorkAboveCpKj : initialWBalExpended;

    let batteryColor = '#10b981'; // Verde
    let batteryStatus = 'Óptima';
    if (minPct < 15) {
        batteryColor = '#ef4444'; // Rojo crítico
        batteryStatus = 'Agotamiento Crítico';
    } else if (minPct < 35) {
        batteryColor = '#f97316'; // Naranja
        batteryStatus = 'Fatiga Severa';
    } else if (minPct < 60) {
        batteryColor = '#eab308'; // Amarillo
        batteryStatus = 'Reserva Moderada';
    }

    return (
        <div style={{
            background: 'rgba(255, 255, 255, 0.015)',
            border: '1px solid var(--glass-border)',
            borderRadius: '12px',
            padding: '1rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.85rem'
        }}>
            {/* Cabecera */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                        padding: '6px',
                        borderRadius: '8px',
                        background: 'rgba(168, 85, 247, 0.1)',
                        border: '1px solid rgba(168, 85, 247, 0.2)',
                        color: '#c084fc',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <BatteryCharging size={16} />
                    </div>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <h4 style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
                                Batería Anaeróbica (W'bal)
                            </h4>
                            <span style={{
                                fontSize: '0.65rem',
                                textTransform: 'uppercase',
                                fontFamily: 'monospace',
                                padding: '1px 6px',
                                borderRadius: '4px',
                                background: 'rgba(168, 85, 247, 0.15)',
                                color: '#d8b4fe',
                                border: '1px solid rgba(168, 85, 247, 0.3)'
                            }}>
                                Modelo Skiba
                            </span>
                        </div>
                        <p style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', margin: 0 }}>
                            Capacidad anaeróbica disponible sobre FTP
                        </p>
                    </div>
                </div>

                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '3px 10px',
                    borderRadius: '8px',
                    border: `1px solid ${batteryColor}40`,
                    background: 'rgba(0, 0, 0, 0.3)'
                }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: batteryColor }} />
                    <span style={{ fontSize: '0.78rem', fontFamily: 'monospace', fontWeight: 700, color: batteryColor }}>
                        {minPct !== undefined && minPct !== null ? `${minPct}% Mín.` : 'N/A'}
                    </span>
                </div>
            </div>

            {/* Tarjetas Bento de Métricas */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.6rem' }}>
                <div style={{
                    background: 'rgba(0, 0, 0, 0.25)',
                    border: '1px solid rgba(255, 255, 255, 0.04)',
                    borderRadius: '8px',
                    padding: '0.6rem 0.75rem',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: 'var(--text-secondary)', fontSize: '0.7rem' }}>
                        <Battery size={13} style={{ color: '#10b981' }} />
                        <span>Mínimo Restante</span>
                    </div>
                    <div style={{ marginTop: '4px' }}>
                        <span style={{ fontSize: '1.15rem', fontWeight: 800, fontFamily: 'monospace', color: '#fff' }}>
                            {data?.minWBalJoules ? `${(data.minWBalJoules / 1000).toFixed(1)} kJ` : `${minPct || 0}%`}
                        </span>
                        <span style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', display: 'block' }}>({batteryStatus})</span>
                    </div>
                </div>

                <div style={{
                    background: 'rgba(0, 0, 0, 0.25)',
                    border: '1px solid rgba(255, 255, 255, 0.04)',
                    borderRadius: '8px',
                    padding: '0.6rem 0.75rem',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: 'var(--text-secondary)', fontSize: '0.7rem' }}>
                        <Flame size={13} style={{ color: '#f59e0b' }} />
                        <span>Trabajo &gt; FTP</span>
                    </div>
                    <div style={{ marginTop: '4px' }}>
                        <span style={{ fontSize: '1.15rem', fontWeight: 800, fontFamily: 'monospace', color: '#f59e0b' }}>
                            {workAboveCp ? `${workAboveCp} kJ` : '0.0 kJ'}
                        </span>
                        <span style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', display: 'block' }}>Energía anaeróbica</span>
                    </div>
                </div>

                <div style={{
                    background: 'rgba(0, 0, 0, 0.25)',
                    border: '1px solid rgba(255, 255, 255, 0.04)',
                    borderRadius: '8px',
                    padding: '0.6rem 0.75rem',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: 'var(--text-secondary)', fontSize: '0.7rem' }}>
                        <Clock size={13} style={{ color: '#ef4444' }} />
                        <span>Batería &lt; 10%</span>
                    </div>
                    <div style={{ marginTop: '4px' }}>
                        <span style={{ fontSize: '1.15rem', fontWeight: 800, fontFamily: 'monospace', color: '#ef4444' }}>
                            {data?.timeUnder10PctSec ? `${Math.round(data.timeUnder10PctSec)} s` : '0 s'}
                        </span>
                        <span style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', display: 'block' }}>Zona de agonía</span>
                    </div>
                </div>
            </div>

            {/* Gráfico interactivo de vaciado y recarga */}
            {data?.curve && data.curve.length > 0 && (
                <div style={{ height: '150px', width: '100%', paddingTop: '4px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={data.curve} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
                            <defs>
                                <linearGradient id="wBalGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#a855f7" stopOpacity={0.4} />
                                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0.02} />
                                </linearGradient>
                            </defs>
                            <XAxis
                                dataKey="timeSec"
                                tickFormatter={formatTime}
                                stroke="#4b5563"
                                tick={{ fill: '#9ca3af', fontSize: 9 }}
                            />
                            <YAxis
                                domain={[0, 100]}
                                unit="%"
                                stroke="#4b5563"
                                tick={{ fill: '#9ca3af', fontSize: 9 }}
                            />
                            <Tooltip
                                content={({ active, payload }) => {
                                    if (active && payload && payload.length) {
                                        const p = payload[0].payload;
                                        return (
                                            <div style={{
                                                background: 'rgba(15, 23, 42, 0.95)',
                                                border: '1px solid rgba(255, 255, 255, 0.15)',
                                                padding: '8px 10px',
                                                borderRadius: '8px',
                                                fontSize: '0.75rem',
                                                color: '#fff'
                                            }}>
                                                <p style={{ margin: 0, color: 'var(--text-secondary)', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                                    Tiempo: {formatTime(p.timeSec)}
                                                </p>
                                                <p style={{ margin: '2px 0', fontWeight: 700, color: '#d8b4fe' }}>
                                                    Batería: {p.wBalPct}% ({(p.wBalJoules / 1000).toFixed(1)} kJ)
                                                </p>
                                                <p style={{ margin: 0, color: '#f59e0b', fontFamily: 'monospace' }}>
                                                    Potencia: {p.watts} W
                                                </p>
                                            </div>
                                        );
                                    }
                                    return null;
                                }}
                            />
                            <Area
                                type="monotone"
                                dataKey="wBalPct"
                                stroke="#c084fc"
                                strokeWidth={2}
                                fillOpacity={1}
                                fill="url(#wBalGrad)"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
}
